import React, { createContext } from 'react';

// ============================================================================
// UTILITY FUNCTIONS FOR FLOATING ELEMENTS & KEYBOARD MANAGEMENT
// ============================================================================

export interface FloatingElementConfig {
  selector: string;
  defaultBottomPosition: number;
  keyboardOffsetTop: number;
}

/**
 * Detect actual keyboard height by measuring viewport changes
 */
export const detectKeyboardHeight = (): number => {
  const screenHeight = window.screen.availHeight;
  const currentHeight = window.innerHeight;
  const estimatedKeyboardHeight = Math.max(0, screenHeight - currentHeight);

  if (estimatedKeyboardHeight > 50) {
    return estimatedKeyboardHeight + 44; // Add buffer for iOS keyboard accessory bar
  }

  const isIPad = /iPad/.test(navigator.userAgent);
  return (isIPad ? 350 : 280) + 44; // Fallback: typical iOS keyboard heights + accessory bar
};

/**
 * Check if device is iOS
 */
export const isIOSDevice = (): boolean => {
  return /(iPad|iPhone|iPod)/.test(navigator.userAgent);
};

/**
 * Update single floating element position
 */
export const updateElementPosition = (
  element: HTMLElement | null,
  config: FloatingElementConfig,
  keyboardHeight: number
): void => {
  if (!element) return;

  const newPosition =
    keyboardHeight > 0
      ? keyboardHeight + config.keyboardOffsetTop
      : config.defaultBottomPosition;

  element.style.bottom = `${newPosition}px`;
};

/**
 * Update multiple floating elements at once
 */
export const updateFloatingElements = (
  configs: FloatingElementConfig[],
  keyboardHeight: number
): void => {
  configs.forEach((config) => {
    const element = document.querySelector(config.selector) as HTMLElement;
    updateElementPosition(element, config, keyboardHeight);
  });
};

/**
 * Predefined floating element configurations
 */
export const FLOATING_CONFIGS = {
  TAB_BAR: {
    selector: 'ion-tab-bar',
    defaultBottomPosition: 16,
    keyboardOffsetTop: 44,
  } as FloatingElementConfig,

  FAB: {
    selector: 'ion-fab[class*="calendarFab"]',
    defaultBottomPosition: 110,
    keyboardOffsetTop: 154,
  } as FloatingElementConfig,

  FLOATING_SEARCHBAR: {
    selector: 'ion-card[class*="floatingSearchBar"]',
    defaultBottomPosition: 80,
    keyboardOffsetTop: 109,
  } as FloatingElementConfig,
};

// ============================================================================
// CONTEXT & HOOK
// ============================================================================

export interface KeyboardContextType {
  keyboardHeight: number;
  setKeyboardHeight: (height: number) => void;
}

export const KeyboardContext = createContext<KeyboardContextType | undefined>(undefined);

export const useKeyboard = (): KeyboardContextType => {
  const context = React.useContext(KeyboardContext);
  if (context === undefined) {
    throw new Error('useKeyboard must be used within a KeyboardProvider');
  }
  return context;
};
